import {
  Box,
  Button,
  FormLabel,
  HStack,
  Spinner,
  Stack,
  useDisclosure,
  useToast,
} from '@chakra-ui/react';
import React, { useEffect, useState } from 'react';
import { useHistory, useParams } from 'react-router-dom';
import { Heading, Input } from '~/components';
import { TextEditor } from '~/components/TextEditor/TextEditor';
import { queryClient } from '~/utils/react-query';
import { ConfirmationDialog } from '~/components/ConfirmationDialog';
import {
  cratePrivacyPolicy,
  getActualPrivacyPolicy,
  getPrivacyPolicyById,
  updatePrivacyPolicy,
} from '~/services/PrivacyPolicy';

export const NewPrivacyPolicy: React.VFC = () => {
  const history = useHistory();
  const {
    isOpen: isOpenModal,
    onOpen: onOpenModal,
    onClose: onCloseModal,
  } = useDisclosure();

  const { id } = useParams<{ id: string }>();
  const [isFetching, setIsFetching] = useState(false);
  const [descricao, setDescricao] = useState('');
  const [mensagem, setMensagem] = useState('');
  const toast = useToast();

  useEffect(() => {
    if (id) {
      setIsFetching(true);
      getPrivacyPolicyById(id)
        .then(response => {
          setDescricao(response.descricao);
          setMensagem(response.mensagem);
        })
        .finally(() => setIsFetching(false));
    } else {
      setIsFetching(true);
      getActualPrivacyPolicy()
        .then(res => {
          if (!res || !res.id) return;
          setDescricao(res.descricao);
          setMensagem(res.mensagem);
        })
        .finally(() => setIsFetching(false));
    }
  }, []);

  const validarCampos = () => {
    let isValid = true;
    const auxMensagem = mensagem === '<p><br></p>' ? '' : mensagem;
    if (!auxMensagem) {
      toast({
        title: 'Campo termos de uso é obrigatório',
        status: 'error',
        duration: 3000,
        isClosable: true,
        position: 'top-right',
      });
      isValid = false;
    }
    if (!descricao.trim()) {
      toast({
        title: 'Campo descrição é obrigatório',
        status: 'error',
        duration: 3000,
        isClosable: true,
        position: 'top-right',
      });
      isValid = false;
    }
    if (descricao.length > 255) {
      toast({
        title: 'Campo descrição deve conter no máximo 255 caracteres',
        status: 'error',
        duration: 3000,
        isClosable: true,
        position: 'top-right',
      });
      isValid = false;
    }
    return isValid;
  };

  const criar = (publicar = false) => {
    if (!validarCampos()) return;
    let request;
    if (id) {
      request = updatePrivacyPolicy(atualizacaoObj(publicar));
    } else {
      request = cratePrivacyPolicy(atualizacaoObj(publicar));
    }

    setIsFetching(true);
    request
      .then(response => {
        toast({
          title: `Atualização ${
            publicar ? 'publicada' : id ? 'editada' : 'salva'
          } com sucesso!`,
          status: 'success',
          duration: 3000,
          isClosable: true,
          position: 'top-right',
        });
        queryClient.invalidateQueries('privacypolicy');
        if (publicar) history.push('/portal/admin/privacy-policy');
        else if (id) voltar();
        else history.push('/portal/admin/privacy-policy');
      })
      .catch(err => {
        const errorsValidate = Object.entries(err.response.data);
        errorsValidate.forEach(_err => {
          toast({
            title: `Erro ao ${
              publicar ? 'publicar' : id ? 'editar' : 'salvar'
            } termos de uso`,
            description: String(_err[1]),
            status: 'error',
            duration: 3000,
            isClosable: true,
            position: 'top-right',
          });
        });
      })
      .finally(() => {
        setIsFetching(false);
        onCloseModal();
      });
  };

  const atualizacaoObj = (publicar = false) => {
    const auxMensagem = mensagem === '<p><br></p>' ? '' : mensagem;
    return {
      descricao,
      mensagem: auxMensagem,
      status: publicar ? 1 : 0,
      id: id ? Number(id) : undefined,
    };
  };

  const voltar = () => {
    history.goBack();
  };

  return (
    <Box px="8" py="4">
      <HStack gridGap={3}>
        <Heading>{id ? 'Editar termo de uso' : 'Novo termo de uso'}</Heading>
        {isFetching && <Spinner />}
      </HStack>
      <Stack py={8} w="70%" gridGap={4}>
        <Stack>
          <FormLabel margin={0} fontSize="14px">
            Descrição: <span style={{ color: 'red' }}>*</span>
          </FormLabel>
          <Input
            name="descricao"
            type="text"
            value={descricao}
            onChange={e => setDescricao(e.target.value)}
            fontSize="15px"
            isDisabled={isFetching}
          />
        </Stack>
        <Stack>
          <FormLabel margin={0} fontSize="14px">
            Termos de Uso: <span style={{ color: 'red' }}>*</span>
          </FormLabel>
          <TextEditor value={mensagem} onChange={setMensagem} />
        </Stack>
        <HStack justifyContent="space-between" gridGap={3} mt={3}>
          <Button
            variant="outline"
            onClick={() => voltar()}
            isDisabled={isFetching}
          >
            Cancelar
          </Button>
          <HStack>
            <Button
              variant="outline"
              onClick={() => criar()}
              isDisabled={isFetching}
            >
              Salvar rascunho
            </Button>
            <Button
              colorScheme="b"
              onClick={() => {
                if (!validarCampos()) return;
                onOpenModal();
              }}
              isLoading={isFetching}
              isDisabled={isFetching}
            >
              Publicar
            </Button>
          </HStack>
        </HStack>
      </Stack>
      <ConfirmationDialog
        confirmar={() => criar(true)}
        onClose={onCloseModal}
        isOpen={isOpenModal}
        titulo="Publicar atualização"
        btnConfirmText="Publicar"
        isDisabled={isFetching}
      >
        <span>
          Tem certeza que deseja publicar essa atualização? Ao confirmar, esta
          ação não poderá ser desfeita.
        </span>
      </ConfirmationDialog>
    </Box>
  );
};
